import { useI18n } from '@affine/i18n';
import type { ViewConfigRow } from '@streamlain/view-config';
import { type FC, useCallback } from 'react';

import type { ViewConfigRowStore } from '../../stores/view-config-row.store';
import type { ComponentCapability } from '../../types/capability-schema';
import type { SetFieldFn } from './hooks/use-view-config-row';
import * as css from './row-form.css';
import { Section } from './section';
import { BindingsSection } from './sections/bindings';
import { FormSection } from './sections/form';
import { HeroSection } from './sections/hero';
import { InlineEditSection } from './sections/inline-edit';
import { LayoutSection } from './sections/layout';
import { NavigationSection } from './sections/navigation';
import { PaginationSection } from './sections/pagination';
import { RowDetailSection } from './sections/row-detail';
import { SearchSection } from './sections/search';
import { SeoSection } from './sections/seo';
import { TagsSection } from './sections/tags';
import type { ContentDbColumn } from './utils/get-content-db-columns';

interface ViewConfigRowFormProps {
  row: ViewConfigRow;
  rowStore: ViewConfigRowStore;
  columns?: ReadonlyArray<ContentDbColumn>;
  activeCapabilities?: ReadonlyArray<ComponentCapability>;
}

interface TextFieldProps {
  field: keyof ViewConfigRow;
  label: string;
  value: string;
  onChange: (v: string) => void;
}

const TextField: FC<TextFieldProps> = ({ field, label, value, onChange }) => (
  <label className={css.field}>
    <span className={css.fieldLabel}>{label}</span>
    <input
      type="text"
      className={css.textInput}
      data-sl-field={String(field)}
      value={value}
      onChange={e => onChange(e.target.value)}
    />
  </label>
);

interface CheckboxFieldProps {
  field: keyof ViewConfigRow;
  label: string;
  value: boolean;
  onChange: (v: boolean) => void;
}

const CheckboxField: FC<CheckboxFieldProps> = ({
  field,
  label,
  value,
  onChange,
}) => (
  <label className={css.checkbox}>
    <input
      type="checkbox"
      data-sl-field={String(field)}
      checked={value}
      onChange={e => onChange(e.target.checked)}
    />
    <span>{label}</span>
  </label>
);

const EMPTY_COLUMNS: ReadonlyArray<ContentDbColumn> = [];
const EMPTY_CAPABILITIES: ReadonlyArray<ComponentCapability> = [];

export const ViewConfigRowForm: FC<ViewConfigRowFormProps> = ({
  row,
  rowStore,
  columns = EMPTY_COLUMNS,
  activeCapabilities = EMPTY_CAPABILITIES,
}) => {
  const t = useI18n();
  const setField = useCallback<SetFieldFn>(
    (field, value) => rowStore.updateField(row.viewId, field, value),
    [rowStore, row.viewId]
  );

  return (
    <form className={css.form} data-testid="view-config-form">
      <Section
        id="general"
        title={t['com.streamlain.customApps.viewConfig.section.general']()}
      >
        <TextField
          field="name"
          label={t['com.streamlain.customApps.viewConfig.general.name']()}
          value={row.name}
          onChange={v => setField('name', v)}
        />
        <TextField
          field="slugPrefix"
          label={t['com.streamlain.customApps.viewConfig.general.slugPrefix']()}
          value={row.slugPrefix ?? ''}
          onChange={v => setField('slugPrefix', v || undefined)}
        />
        <CheckboxField
          field="isDefault"
          label={t[
            'com.streamlain.customApps.viewConfig.general.defaultView'
          ]()}
          value={row.isDefault}
          onChange={v => setField('isDefault', v)}
        />
      </Section>

      <Section
        id="layout"
        title={t['com.streamlain.customApps.viewConfig.section.layout']()}
      >
        <LayoutSection row={row} setField={setField} />
        <RowDetailSection row={row} setField={setField} />
        <InlineEditSection row={row} setField={setField} />
      </Section>

      <Section
        id="navigation"
        title={t['com.streamlain.customApps.viewConfig.section.navigation']()}
      >
        <NavigationSection row={row} setField={setField} />
      </Section>

      <Section
        id="search"
        title={t['com.streamlain.customApps.viewConfig.section.search']()}
      >
        <SearchSection row={row} setField={setField} />
      </Section>

      <Section
        id="pagination"
        title={t['com.streamlain.customApps.viewConfig.section.pagination']()}
      >
        <PaginationSection row={row} setField={setField} />
      </Section>

      <Section
        id="hero"
        title={t['com.streamlain.customApps.viewConfig.section.hero']()}
      >
        <HeroSection row={row} setField={setField} />
      </Section>

      <Section
        id="tags"
        title={t['com.streamlain.customApps.viewConfig.section.tags']()}
      >
        <TagsSection row={row} setField={setField} />
      </Section>

      <Section
        id="form"
        title={t['com.streamlain.customApps.viewConfig.section.form']()}
      >
        <FormSection row={row} setField={setField} />
      </Section>

      <Section
        id="seo"
        title={t['com.streamlain.customApps.viewConfig.section.seo']()}
      >
        <SeoSection row={row} setField={setField} columns={columns} />
      </Section>

      {/* sl-ya4v.4b-E (Cluster E removal): per-view DomainSection deleted.
          Custom domains now bind to a view via custom_domains.custom_app_id
          FK and are managed in workspace settings → Public URL. */}

      <Section
        id="bindings"
        title={t['com.streamlain.customApps.viewConfig.section.bindings']()}
      >
        <BindingsSection
          row={row}
          setField={setField}
          columns={columns}
          activeCapabilities={activeCapabilities}
        />
      </Section>
    </form>
  );
};
